import clsx from "clsx";
import { useState, type FormEvent } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";

import { useAuth } from "../lib/auth";
import { DISCLAIMER, NPRM_NOTICE, ROLE_LABELS, type RoleName } from "../lib/cra";
import { singleCusip } from "../lib/cusip";
import { ChangePasswordButton } from "./ChangePassword";
import { TenantSwitcher } from "./TenantSwitcher";

const NAV = [
  { to: "/screen", label: "Screen" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/peers", label: "Peers" },
  { to: "/memos", label: "Memos" },
] as const;

/** Header quick-jump: type a CUSIP to open its bond page. */
function CusipJump() {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [invalid, setInvalid] = useState(false);
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Same parser as the paste box: an 8-character entry gets its check digit (or Excel's
    // dropped leading zero back) so the bond page never opens on a CUSIP the API rejects.
    const c = singleCusip(value);
    setInvalid(c === null);
    if (c) {
      navigate(`/bonds/${c}`);
      setValue("");
    }
  }
  return (
    <form onSubmit={onSubmit} role="search" className="hidden md:block">
      <input
        aria-label="Open CUSIP"
        aria-invalid={invalid || undefined}
        title={invalid ? "Not a valid CUSIP (check digit or length)" : undefined}
        placeholder="Open CUSIP…"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setInvalid(false);
        }}
        className="h-7 w-36 rounded-md border border-zinc-300 bg-white aria-[invalid]:border-rose-600 px-2 font-mono text-xs uppercase placeholder:font-sans placeholder:normal-case placeholder:text-zinc-400 focus:border-sky-600 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900"
      />
    </form>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "platform_admin" || user?.role === "tenant_admin";
  const items = isAdmin ? [...NAV, { to: "/admin", label: "Admin" } as const] : NAV;

  return (
    <div className="flex min-h-screen flex-col bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-1 focus:text-sm">
        Skip to content
      </a>
      <header className="border-b border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5">
          <NavLink to="/screen" className="flex items-baseline gap-2">
            <span className="text-[15px] font-semibold tracking-tight">CRA Bond ID</span>
            <span className="hidden text-xs text-zinc-500 lg:inline">qualified-investment screening</span>
          </NavLink>
          <nav aria-label="Main" className="flex flex-wrap gap-0.5">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  clsx(
                    "rounded-md px-2.5 py-1 text-sm font-medium",
                    isActive
                      ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                      : "text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <CusipJump />
            <TenantSwitcher />
            {user ? (
              <span className="hidden text-right text-xs leading-tight text-zinc-500 sm:block dark:text-zinc-400">
                {user.name ?? user.email}
                <span className="block">
                  {ROLE_LABELS[user.role as RoleName] ?? user.role}
                  {user.tenant ? ` · ${user.tenant.name}` : ""}
                </span>
              </span>
            ) : null}
            <ChangePasswordButton />
            <button
              type="button"
              className="rounded-md border border-zinc-300 px-2.5 py-1 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main id="main" className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-5">
        <Outlet />
      </main>
      <footer className="border-t border-zinc-200 bg-white px-4 py-3 text-xs text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-4 gap-y-1">
          <p role="note" className="font-medium text-zinc-800 dark:text-zinc-200">
            {DISCLAIMER}
          </p>
          <p>
            Determinations apply the 1995 CRA rule (12 CFR 25/228/345 App. G) and the 2016
            Interagency Q&amp;A. OCC/FDIC 2026 NPRM outputs are a what-if: {NPRM_NOTICE}.
          </p>
          {/* Wording required by the Fannie Mae Developer Portal API terms (Pool Prefix API). */}
          <p>
            This product or service uses data from The Exchange™ by Fannie Mae but is not
            endorsed or certified by Fannie Mae.
          </p>
        </div>
      </footer>
    </div>
  );
}
